import { CreateWorkListsRequestLocalStorage } from '@model/laboratory/worklist.model';

/**
 * Worklists Storage Gateway
 *
 * Abstract contract for worklists state storage operations.
 * This port is defined in the domain layer and implemented by adapters
 * in the infrastructure layer.
 *
 * Security requirements:
 * - Implementations should avoid persistent storage (localStorage)
 * - Data should be cleared on logout
 * - TTL expiration should be enforced
 */
export abstract class WorklistsStorageGateway {
  /**
   * Store worklists data
   * @param data - Worklists data to store
   */
  abstract set(data: CreateWorkListsRequestLocalStorage): void;

  /**
   * Get worklists data
   * @returns Stored worklists or null if not found/expired
   */
  abstract get(): CreateWorkListsRequestLocalStorage | null;

  /**
   * Check if worklists data exists and is not expired
   * @returns true if valid data exists
   */
  abstract exists(): boolean;

  /**
   * Remove specific worklists by LG codes
   * @param lgList - Array of LG codes to remove
   */
  abstract deleteByLgList(lgList: string[]): void;

  /**
   * Clear all worklists data
   * Should be called on logout or when data is no longer needed
   */
  abstract clearAll(): void;

  /**
   * Migrate data from old localStorage (if exists) and clear it
   * Call this once during app initialization
   */
  abstract migrateFromLocalStorageAndClear(): void;
}
